import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./app/AuthProvider";
import { App } from "./App";
import "./index.css";

/**
 * Punto de entrada del panel.
 *
 * TanStack Query se configura con `retry: 1` y `refetchOnWindowFocus: false`:
 * es un panel de gestión sobre datos que cambian poco, y refetch agresivo solo
 * añadiría ruido de red. Los 401 no se reintentan — la sesión caída debe
 * llevar al acceso, no a un bucle de reintentos.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 20_000,
    },
  },
});

const container = document.getElementById("root");
if (!container) throw new Error("Falta el contenedor #root en index.html");

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider baseUrl={(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ""}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
